<template>
    <div class="file-upload">
        <!-- dropzone -->
        <div
            class="w-full text-center bg-gray-tier-2 rounded-md border border-keyline-primary p-5"
            :data-active="active"
            @dragenter.prevent="setActive"
            @dragover.prevent="setActive"
            @dragleave.prevent="setInactive"
            @drop.prevent="onDrop"
        >
            <label
                for="file-input"
                class="w-full flex items-center flex-col gap-3"
            >
                <div class="text-center">
                    <base-svg
                        name="upload-cloud"
                        class="h-7 m-auto type-secondary"
                    />
                </div>
                <div>
                    <div v-if="active">
                        <slot name="activeContent">
                            <p class="text-sm">Drop Files</p>
                            <p class="text-xs">
                                (max file size {{ props.maxFileSize }})
                            </p>
                        </slot>
                    </div>
                    <div v-else>
                        <slot name="staticContent">
                            <p class="text-sm type-secondary">
                                Drag &amp; drop your files here or
                                <span class="text-default underline"
                                    >browse</span
                                >
                            </p>
                            <p class="text-xs type-secondary">
                                (max file size {{ props.maxFileSize }})
                            </p>
                        </slot>
                    </div>
                </div>
                <input
                    class="sr-only"
                    type="file"
                    id="file-input"
                    multiple
                    @change="onInputChange"
                />
            </label>
        </div>

        <!-- file list -->
        <div class="file-list">
            <div v-show="files.length" class="my-4 type-secondary">
                <h5 v-if="files.find((file) => file.uploadProgress < 100)">
                    Uploading
                    {{
                        files.filter((file) => file.uploadProgress === 100)
                            .length + 1
                    }}
                    of {{ files.length }}
                </h5>
                <h5 v-else>
                    {{ files.length }} file<span v-if="files.length > 1"
                        >s</span
                    >
                    uploaded
                </h5>
            </div>
            <ul
                v-show="files.length"
                class="w-full flex items-center flex-col gap-3"
            >
                <li
                    v-for="file of files"
                    :key="file.id"
                    class="w-full flex items-center gap-2 bg-gray-tier-2 rounded-md border border-keyline-primary px-3 py-2"
                    :style="
                        file.uploadProgress < 100
                            ? `background:linear-gradient(90deg, #2C9C52 ${file.uploadProgress}%, rgba(0, 0, 0, 0) ${file.uploadProgress}%)`
                            : ''
                    "
                >
                    <file-type :file="file" class="h-4.5 fill-gray-tier-7" />
                    <div class="flex flex-col gap-0.5">
                        <p class="line-clamp-1 leading-none text-xs">
                            {{ file.file.name }}
                        </p>
                        <p
                            class="type-secondary text-2xs uppercase leading-none"
                        >
                            {{ useFormatBytes(file.file.size) }}
                        </p>
                    </div>
                    <div class="ml-auto flex items-center">
                        <div
                            v-if="file.uploadProgress === 100 && (file.saved || !patch_update_route)"
                            class="flex items-center gap-1 mr-3"
                        >
                            <base-svg
                                name="check"
                                class="rounded-full bg-apple fill-white h-3.5 p-0.5"
                            />
                            <span class="text-xs type-secondary">Uploaded</span>
                        </div>
                        <div
                            v-else-if="file.uploadProgress === 100 && (patch_update_route && !file.saved)"
                            class="flex items-center gap-1 mr-3"
                        >
                            <loading-spinner />
                            <span class="text-xs type-secondary">Saving…</span>
                        </div>
                        <button
                            class="flex gap-2 items-center"
                            @click="removeFile(file)"
                        >
                            <base-svg name="close" class="h-4" />
                            <span class="sr-only">Remove</span>
                        </button>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { useFileList, useFileUploader, useFormatBytes } from "@/Composables";
import { BaseSvg, FileType, LoadingSpinner } from "@/Components";
import {computed, inject, onMounted, onUnmounted, ref} from "vue";

const route = inject("route");

const emit = defineEmits(["files-dropped", "file-removed"]);

const props = defineProps({
    maxFileSize: {
        type: String,
        default: "200 MB",
        required: false,
    },
    patch_update_route: {
        type: String,
        required: false,
        default: null,
    },
});

const events = ["dragenter", "dragover", "dragleave", "drop"];

let active = ref(false);
let inactiveTimeout = null;

const { files, addFiles, removeFile } = useFileList();
const { uploadFiles } = useFileUploader(props.patch_update_route);

function setActive() {
    active.value = true;
    clearTimeout(inactiveTimeout);
}

function setInactive() {
    // prevents dragover flicker
    inactiveTimeout = setTimeout(() => {
        active.value = false;
    }, 50);
}

function onDrop($event) {
    const uploaded_files = $event.dataTransfer.files;

    if (checkFileSize(uploaded_files)) {
        setInactive();
        addFiles(uploaded_files);
        uploadFiles([...files.value]);
        emit("files-dropped", [...$event.dataTransfer.files]);
    }
}

function onInputChange($event) {
    const uploaded_files = $event.target.files;

    if (checkFileSize(uploaded_files)) {
        addFiles(uploaded_files);
        uploadFiles([...files.value]);
        $event.target.value = null;
    }
}

function preventDefaults(ev) {
    ev.preventDefault();
}

const maxSizeInBytes = computed(() => {
    return humanFileSizeToInt(props.maxFileSize)
});

function humanFileSizeToInt(fileSizeString) {
    const units = {
        B: 1,
        KB: 1024,
        MB: 1024 * 1024,
        GB: 1024 * 1024 * 1024,
        TB: 1024 * 1024 * 1024 * 1024,
    };

    const match = fileSizeString.match(/^(\d+(?:\.\d+)?)\s*([BKMGT]B)$/i);

    if (!match) {
        throw new Error("Invalid file size string");
    }

    const size = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    return Math.round(size * units[unit]);
}

function checkFileSize(files) {
    for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (file.size > maxSizeInBytes.value) {
            // File is too large, handle the error
            alert(`File "${file.name}" is too large. Maximum allowed size is ${props.maxFileSize}.`);
            return false; // Stop further processing
        }
    }

    return true;
}

onMounted(() => {
    events.forEach((eventName) => {
        document.body.addEventListener(eventName, preventDefaults);
    });
});

onUnmounted(() => {
    events.forEach((eventName) => {
        document.body.removeEventListener(eventName, preventDefaults);
    });
});
</script>
